import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import Autocomplete from '@material-ui/lab/Autocomplete'
import CircularProgress from '@material-ui/core/CircularProgress'
import Button from '@material-ui/core/Button'
import { useSnackbar } from 'notistack'
import { amplitudeEvent } from 'lib/amplitude'
import {
  getAudienceOptions,
  resolveGroupEventMessagingRecipients,
} from 'lib/group-event-messaging/recipients'
import {
  useFindOrCreateChannelWithUserIds,
  useNavigateToChannel,
} from 'lib/stream-messaging/hooks'
import {
  useGroupEventMessagingRecipients,
  useGroupEventOptionsForMessaging,
} from '../api-hooks'
import { toFullDateShortMonth } from 'utils/dateUtils'
import useStyles from './styles'

const getEventLabel = event => {
  const dateLabel = event.startDateTime
    ? toFullDateShortMonth(event.startDateTime)
    : null

  return dateLabel ? `${event.title} (${dateLabel})` : event.title
}

const GroupEventAudienceMessageForm = ({
  event: presetEvent,
  onCancel,
  onSuccess,
  onChannelCreated,
  showActions = true,
}) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const findOrCreateChannelWithUserIds = useFindOrCreateChannelWithUserIds()
  const navigateToChannel = useNavigateToChannel()
  const skipEventOptionsQuery = Boolean(presetEvent)
  const {
    data: eventOptionsData,
    loading: eventOptionsLoading,
    error: eventOptionsError,
    refetch: refetchEventOptions,
  } = useGroupEventOptionsForMessaging({
    skip: skipEventOptionsQuery,
  })

  const [selectedEventOption, setSelectedEventOption] = useState(null)
  const [groupName, setGroupName] = useState('')
  const [includeAttendees, setIncludeAttendees] = useState(false)
  const [includeWaitlist, setIncludeWaitlist] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const isCreatingRef = useRef(false)

  const selectedEventId = presetEvent?.id ?? selectedEventOption?.id
  const {
    data: recipientsData,
    loading: recipientsLoading,
    error: recipientsError,
    refetch: refetchRecipients,
  } = useGroupEventMessagingRecipients({
    id: selectedEventId,
    skip: !selectedEventId || Boolean(presetEvent),
  })

  const availableEvents = useMemo(() => {
    const events = eventOptionsData?.currentUser?.groupEvents ?? []

    return events.filter(event => event.registrationCount > 0)
  }, [eventOptionsData])

  const activeEvent = presetEvent ?? recipientsData?.groupEvents?.[0] ?? null
  const audienceOptions = activeEvent ? getAudienceOptions(activeEvent) : null

  useEffect(() => {
    if (!activeEvent) {
      setGroupName('')
      setIncludeAttendees(false)
      setIncludeWaitlist(false)
      return
    }

    setGroupName(activeEvent.title)
    const options = getAudienceOptions(activeEvent)
    setIncludeAttendees(options.attendees.defaultChecked)
    setIncludeWaitlist(options.waitlist.defaultChecked)
  }, [activeEvent?.id]) // eslint-disable-line react-hooks/exhaustive-deps -- reset when selected event changes

  const recipientCount = activeEvent
    ? resolveGroupEventMessagingRecipients(activeEvent, {
      includeAttendees,
      includeWaitlist,
    }).length
    : 0

  const canCreate =
    Boolean(activeEvent) &&
    (includeAttendees || includeWaitlist) &&
    recipientCount > 0 &&
    !isCreating &&
    !recipientsLoading

  const handleCreate = async () => {
    if (!activeEvent || isCreatingRef.current) return

    const recipientExternalIds = resolveGroupEventMessagingRecipients(
      activeEvent,
      { includeAttendees, includeWaitlist },
    )

    if (recipientExternalIds.length === 0) {
      enqueueSnackbar('Select at least one recipient to message.', {
        variant: 'warning',
      })
      return
    }

    isCreatingRef.current = true
    setIsCreating(true)

    const channelName = groupName.trim() || activeEvent.title

    try {
      const channel = await findOrCreateChannelWithUserIds(
        recipientExternalIds,
        { name: channelName },
      )

      if (channel) {
        amplitudeEvent('group-event-audience-message-created', {
          groupEventId: activeEvent.id,
          channelName,
          includeAttendees,
          includeWaitlist,
          recipientCount: recipientExternalIds.length,
          url: window.location.href,
          path: window.location.pathname,
        })

        if (onChannelCreated) {
          onChannelCreated(channel)
        } else {
          navigateToChannel(channel)
        }
        onSuccess?.()
      } else {
        enqueueSnackbar(
          'Unable to create or join conversation. Please try again later.',
          { variant: 'error' },
        )
      }
    } finally {
      isCreatingRef.current = false
      setIsCreating(false)
    }
  }

  if (!presetEvent && eventOptionsLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    )
  }

  if (!presetEvent && eventOptionsError) {
    return (
      <Box>
        <Typography variant="body1" gutterBottom>
          Unable to load group events
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => refetchEventOptions()}
        >
          Reload
        </Button>
      </Box>
    )
  }

  return (
    <Box className={classes.content}>
      {presetEvent ? (
        <Box className={classes.eventContext}>
          <Typography variant="subtitle2" color="textSecondary">
            Group event
          </Typography>
          <Typography variant="body1">{presetEvent.title}</Typography>
        </Box>
      ) : (
        <Autocomplete
          options={availableEvents}
          value={selectedEventOption}
          onChange={(_event, value) => setSelectedEventOption(value)}
          getOptionLabel={getEventLabel}
          getOptionSelected={(option, value) => option.id === value.id}
          renderInput={params => (
            <TextField
              {...params}
              label="Group event"
              variant="outlined"
              required
            />
          )}
          noOptionsText="No group events with registered attendees"
        />
      )}

      {!presetEvent && selectedEventOption && recipientsLoading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={24} />
        </Box>
      ) : null}

      {!presetEvent && selectedEventOption && recipientsError ? (
        <Box>
          <Typography variant="body2" color="error" gutterBottom>
            Unable to load event recipients
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => refetchRecipients()}
          >
            Reload
          </Button>
        </Box>
      ) : null}

      {activeEvent && audienceOptions ? (
        <>
          <TextField
            className={classes.nameField}
            fullWidth
            inputProps={{ maxLength: 30 }}
            label="Group name"
            onChange={event => setGroupName(event.target.value)}
            value={groupName}
            variant="outlined"
          />
          <Box className={classes.audienceSection}>
            <Typography variant="body2" className={classes.audienceLabel}>
              Send to
            </Typography>
            <FormControlLabel
              className={classes.audienceOption}
              control={
                <Checkbox
                  color="primary"
                  checked={includeAttendees}
                  disabled={!audienceOptions.attendees.available}
                  onChange={event => setIncludeAttendees(event.target.checked)}
                />
              }
              label={`Registered attendees (${audienceOptions.attendees.count})`}
            />
            <FormControlLabel
              className={classes.audienceOption}
              control={
                <Checkbox
                  color="primary"
                  checked={includeWaitlist}
                  disabled={!audienceOptions.waitlist.available}
                  onChange={event => setIncludeWaitlist(event.target.checked)}
                />
              }
              label={`Waitlist (${audienceOptions.waitlist.count})`}
            />
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.helperText}
            >
              This creates a group conversation. Replies are visible to everyone
              in the group. Waitlist contacts are only included when you select
              Waitlist.
              {recipientCount > 0
                ? ` ${recipientCount} recipient${
                  recipientCount === 1 ? '' : 's'
                } selected.`
                : ''}
            </Typography>
          </Box>
        </>
      ) : null}

      {showActions ? (
        <Box
          className={classes.dialogActions}
          display="flex"
          justifyContent="flex-end"
        >
          {onCancel ? (
            <Button onClick={onCancel} disabled={isCreating}>
              Cancel
            </Button>
          ) : null}
          <Button
            className={classes.createButton}
            variant="contained"
            color="primary"
            onClick={handleCreate}
            disabled={!canCreate}
          >
            Create
          </Button>
        </Box>
      ) : null}
    </Box>
  )
}

GroupEventAudienceMessageForm.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    enrollments: PropTypes.array,
    waitlist: PropTypes.shape({
      entries: PropTypes.array,
    }),
  }),
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  onChannelCreated: PropTypes.func,
  showActions: PropTypes.bool,
}

export default GroupEventAudienceMessageForm
